/**
 * AppInventoryDrawer — Phase 3 — per-app override + create-mapping UI.
 *
 * Renders inside a migration capture card. Shows every app from the
 * source's apps.json bucketed into the resolver's 7 categories with
 * per-row overrides, search/filters, and a "Create custom mapping
 * from this row" affordance that pre-fills the mapping form.
 *
 * Heavy lift kept in this file so Migrations.jsx stays under 2 kLOC.
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
    Package, Loader2, Search, Filter, Wrench, FilePlus2,
    RotateCw, RefreshCw, X, ChevronDown, ChevronRight, MoreVertical,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    DropdownMenu, DropdownMenuContent, DropdownMenuItem,
    DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

// 7 resolver categories + a synthetic "total" bucket. Manual & skipped
// are styled as normal workflow states (slate / neutral) — NOT errors.
const CATEGORIES = ["winget", "custom", "msi", "store", "portable", "manual", "skipped"];

const CATEGORY_LABEL = {
    winget: "Winget matched",
    custom: "Custom mapping matched",
    msi: "MSI detected",
    store: "Microsoft Store app",
    portable: "Portable / per-user app",
    manual: "Manual review required",
    skipped: "Skipped / vendor reinstall recommended",
};

// Card + badge styles. Manual / skipped are slate (not rose / amber) so
// they read as expected workflow states, not errors.
const CATEGORY_TONE = {
    winget:   { card: "bg-emerald-950/30 border-emerald-800/40", badge: "bg-emerald-900/40 text-emerald-200 border-emerald-700/50" },
    custom:   { card: "bg-violet-950/30 border-violet-800/40",   badge: "bg-violet-900/40 text-violet-200 border-violet-700/50" },
    msi:      { card: "bg-sky-950/30 border-sky-800/40",         badge: "bg-sky-900/40 text-sky-200 border-sky-700/50" },
    store:    { card: "bg-indigo-950/30 border-indigo-800/40",   badge: "bg-indigo-900/40 text-indigo-200 border-indigo-700/50" },
    portable: { card: "bg-teal-950/30 border-teal-800/40",       badge: "bg-teal-900/40 text-teal-200 border-teal-700/50" },
    manual:   { card: "bg-slate-900/40 border-slate-700/50",     badge: "bg-slate-800/60 text-slate-200 border-slate-600/50" },
    skipped:  { card: "bg-zinc-900/40 border-zinc-700/50",       badge: "bg-zinc-800/60 text-zinc-200 border-zinc-600/50" },
};

const CONFIDENCE_BANDS = [
    { value: "any", label: "Any confidence" },
    { value: "high", label: "High (≥85)" },
    { value: "med", label: "Medium (50–84)" },
    { value: "low", label: "Low (<50)" },
];

export default function AppInventoryDrawer({ captureId, captureStatus }) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [apps, setApps] = useState([]);
    const [summary, setSummary] = useState(null);

    // Filters
    const [search, setSearch] = useState("");
    const [filterCat, setFilterCat] = useState("all");
    const [filterConf, setFilterConf] = useState("any");
    const [filterScope, setFilterScope] = useState("all");
    const [filterSource, setFilterSource] = useState("all");

    // Modal state
    const [mappingModal, setMappingModal] = useState(null); // { app }
    const [detailsApp, setDetailsApp] = useState(null);

    const fetchApps = useCallback(async () => {
        setLoading(true);
        try {
            const r = await http.get(`/migrations/${captureId}/apps`);
            const list = Array.isArray(r.data) ? r.data : (r.data?.apps || []);
            setApps(list);
            setSummary(r.data?.resolution_summary || null);
        } catch (e) {
            if (e?.response?.status !== 404) {
                toast.error("Failed to load apps: " + errMsg(e));
            }
            setApps([]);
            setSummary(null);
        } finally {
            setLoading(false);
        }
    }, [captureId]);

    useEffect(() => {
        if (!open) return;
        fetchApps();
    }, [open, fetchApps]);

    const setOverride = useCallback(async (app, body) => {
        try {
            await http.post(
                `/migrations/${captureId}/apps/${encodeURIComponent(app.app_key)}/override`,
                body,
            );
            toast.success(body.category ? `Override applied: ${CATEGORY_LABEL[body.category]}` : "Override saved");
            fetchApps();
        } catch (e) {
            toast.error("Override failed: " + errMsg(e));
        }
    }, [captureId, fetchApps]);

    const clearOverride = useCallback(async (app) => {
        try {
            await http.delete(
                `/migrations/${captureId}/apps/${encodeURIComponent(app.app_key)}/override`,
            );
            toast.success("Override cleared");
            fetchApps();
        } catch (e) {
            toast.error("Clear failed: " + errMsg(e));
        }
    }, [captureId, fetchApps]);

    const reresolve = useCallback(async (app) => {
        try {
            const r = await http.post(
                `/migrations/${captureId}/apps/${encodeURIComponent(app.app_key)}/resolve`,
            );
            const cat = r.data?.resolved?.category;
            toast.success(`Resolver: ${CATEGORY_LABEL[cat] || cat}`);
            fetchApps();
        } catch (e) {
            toast.error("Re-resolve failed: " + errMsg(e));
        }
    }, [captureId, fetchApps]);

    // ---- filters ----
    const filtered = useMemo(() => {
        const q = (search || "").trim().toLowerCase();
        return apps.filter(a => {
            if (filterCat !== "all" && a.category !== filterCat) return false;
            if (filterScope !== "all" && (a.install_scope || "") !== filterScope) return false;
            if (filterSource !== "all" && (a.app_source || "") !== filterSource) return false;
            if (filterConf !== "any") {
                const c = a.confidence ?? 0;
                if (filterConf === "high" && c < 85) return false;
                if (filterConf === "med"  && (c < 50 || c >= 85)) return false;
                if (filterConf === "low"  && c >= 50) return false;
            }
            if (!q) return true;
            const hay = [
                a.name, a.publisher, a.winget_id, a.msi_product_code,
                a.package_family_name,
            ].filter(Boolean).join(" ").toLowerCase();
            return hay.includes(q);
        });
    }, [apps, search, filterCat, filterConf, filterScope, filterSource]);

    // Source / scope options derived from inventory.
    const sourceOpts = useMemo(() => {
        const s = new Set(apps.map(a => a.app_source).filter(Boolean));
        return ["all", ...Array.from(s).sort()];
    }, [apps]);
    const scopeOpts = useMemo(() => {
        const s = new Set(apps.map(a => a.install_scope).filter(Boolean));
        return ["all", ...Array.from(s).sort()];
    }, [apps]);

    const totals = summary || {};
    if (captureStatus !== "completed" && captureStatus !== "capturing") return null;

    return (
        <div className="mt-4" data-testid={`app-inventory-drawer-${captureId}`}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between p-3 rounded-md border bg-zinc-950/30 border-zinc-800 hover:brightness-110 transition"
                data-testid={`app-inventory-toggle-${captureId}`}
            >
                <div className="flex items-center gap-2 text-sm">
                    <Package className="w-4 h-4 text-zinc-400" />
                    <span className="text-zinc-200 font-medium">App Inventory</span>
                    {totals?.total > 0 && (
                        <span className="text-xs text-zinc-500">
                            · {totals.total} total · {totals.winget || 0} winget · {totals.manual || 0} manual · {totals.skipped || 0} skipped
                        </span>
                    )}
                </div>
                <span className="text-zinc-500">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
            </button>

            {open && (
                <div className="mt-3 space-y-3" data-testid={`app-inventory-body-${captureId}`}>
                    {/* Category cards */}
                    {summary && (
                        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
                            <CategoryCount label="Total" value={totals.total} tone="bg-zinc-900/40 border-zinc-700/50"
                                active={filterCat === "all"} onClick={() => setFilterCat("all")} testid="cat-card-total" />
                            {CATEGORIES.map(cat => (
                                <CategoryCount
                                    key={cat}
                                    label={CATEGORY_LABEL[cat]}
                                    value={totals[cat] || 0}
                                    tone={CATEGORY_TONE[cat].card}
                                    active={filterCat === cat}
                                    onClick={() => setFilterCat(filterCat === cat ? "all" : cat)}
                                    testid={`cat-card-${cat}`}
                                />
                            ))}
                        </div>
                    )}

                    {/* Filters / search */}
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="relative flex-1 min-w-[240px]">
                            <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search name, publisher, winget ID, MSI code, package family…"
                                className="pl-8 h-9 bg-zinc-950/50"
                                data-testid="inv-search"
                            />
                        </div>
                        <Select value={filterConf} onValueChange={setFilterConf}>
                            <SelectTrigger className="h-9 w-[160px]" data-testid="inv-filter-confidence"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CONFIDENCE_BANDS.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={filterScope} onValueChange={setFilterScope}>
                            <SelectTrigger className="h-9 w-[140px]" data-testid="inv-filter-scope"><SelectValue placeholder="Scope" /></SelectTrigger>
                            <SelectContent>
                                {scopeOpts.map(s => <SelectItem key={s} value={s}>{s === "all" ? "Any scope" : s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Select value={filterSource} onValueChange={setFilterSource}>
                            <SelectTrigger className="h-9 w-[180px]" data-testid="inv-filter-source"><SelectValue placeholder="Source" /></SelectTrigger>
                            <SelectContent>
                                {sourceOpts.map(s => <SelectItem key={s} value={s}>{s === "all" ? "Any source" : s}</SelectItem>)}
                            </SelectContent>
                        </Select>
                        <Button size="sm" variant="outline" onClick={fetchApps} disabled={loading} data-testid="inv-refresh-btn">
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                        </Button>
                    </div>

                    {/* Rows */}
                    <div className="rounded-md border border-zinc-800 max-h-[420px] overflow-y-auto">
                        {loading && apps.length === 0 ? (
                            <div className="p-6 text-center text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div className="p-6 text-center text-sm text-zinc-500">
                                {apps.length === 0 ? "No apps captured yet." : "No apps match the current filters."}
                            </div>
                        ) : filtered.map(app => (
                            <AppRow
                                key={app.app_key}
                                app={app}
                                onChangeCategory={(cat) => setOverride(app, { category: cat })}
                                onSetWingetId={(winget_id) => setOverride(app, { category: "winget", winget_id })}
                                onClearOverride={() => clearOverride(app)}
                                onReresolve={() => reresolve(app)}
                                onCreateMapping={() => setMappingModal({ app })}
                                onViewDetails={() => setDetailsApp(app)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {mappingModal && (
                <CreateMappingFromAppModal
                    captureId={captureId}
                    app={mappingModal.app}
                    onClose={() => setMappingModal(null)}
                    onSaved={() => { setMappingModal(null); fetchApps(); }}
                />
            )}
            {detailsApp && (
                <AppDetailsDrawer app={detailsApp} onClose={() => setDetailsApp(null)} />
            )}

            {/* Phase 4: Apply Plan preview accordion. Renders inside the
                inventory drawer so operators can see exactly what the
                destination will do before they hit Apply. */}
            {open && <ApplyPlanPreview captureId={captureId} />}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Phase 4: ApplyPlanPreview — ordered queue, grouped by category, surfaces
// install_action + warnings (sensitive_class_override etc) before Apply.
// ---------------------------------------------------------------------------
const ACTION_LABEL = {
    winget_install: "winget install",
    custom_installer: "Custom installer",
    custom_winget: "Custom mapping → winget",
    msi_install: "MSI install",
    manual_msi_source_required: "Manual — attach MSI installer",
    store_reinstall: "Store reinstall",
    manual_store_reinstall: "Manual — Store reinstall",
    portable_restore: "Restore portable folder",
    manual_portable_restore: "Manual — copy portable folder",
    manual_checklist: "Manual checklist",
    vendor_checklist: "Vendor reinstall checklist",
};

function ApplyPlanPreview({ captureId }) {
    const [open, setOpen] = useState(false);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || plan) return;
        setLoading(true);
        http.get(`/migrations/${captureId}/apply-plan`)
            .then(r => setPlan(r.data))
            .catch(e => {
                if (e?.response?.status !== 404) toast.error("Apply plan failed: " + errMsg(e));
            })
            .finally(() => setLoading(false));
    }, [open, plan, captureId]);

    return (
        <div className="mt-3" data-testid={`apply-plan-${captureId}`}>
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                className="w-full flex items-center justify-between p-2.5 rounded-md border bg-zinc-950/30 border-zinc-800 hover:brightness-110 transition text-sm"
                data-testid={`apply-plan-toggle-${captureId}`}
            >
                <div className="flex items-center gap-2">
                    <RotateCw className="w-4 h-4 text-zinc-400" />
                    <span className="text-zinc-200 font-medium">Apply Plan Preview</span>
                    {plan?.total > 0 && (
                        <span className="text-xs text-zinc-500">· {plan.total} apps · {plan.category_order.length} categor{plan.category_order.length === 1 ? "y" : "ies"}</span>
                    )}
                </div>
                <span className="text-zinc-500">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</span>
            </button>
            {open && (
                <div className="mt-2 rounded-md border border-zinc-800 max-h-[380px] overflow-y-auto">
                    {loading ? (
                        <div className="p-4 text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Building plan…</div>
                    ) : !plan ? (
                        <div className="p-4 text-sm text-zinc-500">Apply plan unavailable.</div>
                    ) : plan.category_order.map(cat => (
                        <div key={cat} className="border-b border-zinc-800 last:border-0">
                            <div className={`px-3 py-1.5 text-[11px] uppercase tracking-wide font-semibold ${CATEGORY_TONE[cat]?.badge || "bg-zinc-900/40 text-zinc-300"}`}>
                                {CATEGORY_LABEL[cat]} · {plan.groups[cat].length}
                            </div>
                            {plan.groups[cat].map(it => {
                                const a = it.action || {};
                                const warn = (a.warnings || []).includes("sensitive_class_override");
                                return (
                                    <div key={it.app_key} className="px-3 py-1.5 text-xs flex items-center gap-3 border-t border-zinc-800/50" data-testid={`apply-plan-row-${it.app_key}`}>
                                        <span className="flex-1 truncate text-zinc-200">{it.name}{it.version ? <span className="text-zinc-500"> v{it.version}</span> : null}</span>
                                        <span className="text-zinc-400 text-[11px]">{ACTION_LABEL[a.action] || a.action}</span>
                                        {warn && (
                                            <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-700/50 text-amber-300 bg-amber-950/30">
                                                ⚠ sensitive
                                            </Badge>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function CategoryCount({ label, value, tone, active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={`text-left p-2 rounded-md border transition ${tone} ${active ? "ring-2 ring-emerald-500/50" : "hover:brightness-110"}`}
        >
            <div className="text-[10px] uppercase tracking-wide text-zinc-400 truncate">{label}</div>
            <div className="text-lg font-semibold text-zinc-100">{value ?? 0}</div>
        </button>
    );
}

function AppRow({ app, onChangeCategory, onSetWingetId, onClearOverride, onReresolve, onCreateMapping, onViewDetails }) {
    const tone = CATEGORY_TONE[app.category] || CATEGORY_TONE.manual;
    const isOverridden = !!app.override?.category;
    return (
        <div
            className="flex items-center gap-3 px-3 py-2 border-b border-zinc-800 last:border-0 hover:bg-zinc-900/40"
            data-testid={`inv-row-${app.app_key}`}
        >
            <div className="flex-1 min-w-0">
                <div className="text-sm text-zinc-200 truncate flex items-center gap-2">
                    {app.name}
                    {app.version && <span className="text-xs text-zinc-500">v{app.version}</span>}
                    {isOverridden && (
                        <Badge variant="outline" className="text-[10px] py-0 h-4 border-amber-700/50 text-amber-300 bg-amber-950/30">
                            Overridden
                        </Badge>
                    )}
                </div>
                <div className="text-xs text-zinc-500 truncate flex items-center gap-2 flex-wrap">
                    {app.publisher && <span>{app.publisher}</span>}
                    {app.winget_id && <span className="text-emerald-400">winget: {app.winget_id}</span>}
                    {app.msi_product_code && <span className="text-sky-400">MSI</span>}
                    {app.package_family_name && <span className="text-indigo-400">store</span>}
                    {app.install_scope && <span>· {app.install_scope}</span>}
                    {app.architecture && <span>· {app.architecture}</span>}
                </div>
            </div>
            <Badge variant="outline" className={`text-[11px] ${tone.badge}`}>
                {app.category_label || app.category}
            </Badge>
            <span className="text-xs text-zinc-500 w-12 text-right tabular-nums">{app.confidence ?? 0}%</span>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-7 h-7" data-testid={`inv-row-actions-${app.app_key}`}>
                        <MoreVertical className="w-4 h-4" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="text-xs">Change category</DropdownMenuLabel>
                    {CATEGORIES.map(cat => (
                        <DropdownMenuItem
                            key={cat}
                            onSelect={() => onChangeCategory(cat)}
                            disabled={app.category === cat && !isOverridden}
                            data-testid={`inv-set-${cat}-${app.app_key}`}
                        >
                            {CATEGORY_LABEL[cat]}
                        </DropdownMenuItem>
                    ))}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onSelect={onCreateMapping} data-testid={`inv-create-mapping-${app.app_key}`}>
                        <FilePlus2 className="w-4 h-4 mr-2" /> Create custom mapping from row
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={onReresolve} data-testid={`inv-reresolve-${app.app_key}`}>
                        <RotateCw className="w-4 h-4 mr-2" /> Re-resolve this app
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={onViewDetails} data-testid={`inv-details-${app.app_key}`}>
                        <Wrench className="w-4 h-4 mr-2" /> View resolver details
                    </DropdownMenuItem>
                    {isOverridden && (
                        <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onSelect={onClearOverride} className="text-rose-300" data-testid={`inv-clear-override-${app.app_key}`}>
                                <X className="w-4 h-4 mr-2" /> Clear override
                            </DropdownMenuItem>
                        </>
                    )}
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    );
}

function CreateMappingFromAppModal({ captureId, app, onClose, onSaved }) {
    const [installerType, setInstallerType] = useState(app.winget_id ? "winget" : "exe");
    const [wingetId, setWingetId] = useState(app.winget_id || "");
    const [installerPath, setInstallerPath] = useState("");
    const [silentArgs, setSilentArgs] = useState("");
    const [notes, setNotes] = useState("");
    const [confidence, setConfidence] = useState(95);
    const [autoLink, setAutoLink] = useState(true);
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await http.post(
                `/migrations/${captureId}/apps/${encodeURIComponent(app.app_key)}/create-mapping`,
                {
                    installerType,
                    wingetId: wingetId || undefined,
                    installerPath: installerPath || undefined,
                    silentArgs: silentArgs || undefined,
                    notes: notes || undefined,
                    confidence,
                    auto_link_override: autoLink,
                },
            );
            toast.success("Custom mapping created");
            onSaved();
        } catch (e) {
            toast.error("Save failed: " + errMsg(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="create-mapping-from-app-dialog" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>Create Custom Mapping</DialogTitle>
                    <DialogDescription>
                        Pre-filled from <span className="text-zinc-200">{app.name}</span>
                        {app.publisher && <> — {app.publisher}</>}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-1">
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Display name (locked)">
                            <Input value={app.name} disabled className="h-9 bg-zinc-950/40" />
                        </Field>
                        <Field label="Publisher (locked)">
                            <Input value={app.publisher || ""} disabled className="h-9 bg-zinc-950/40" />
                        </Field>
                    </div>
                    <Field label="Installer type">
                        <Select value={installerType} onValueChange={setInstallerType}>
                            <SelectTrigger className="h-9" data-testid="cm-installer-type"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="winget">Winget</SelectItem>
                                <SelectItem value="msi">MSI</SelectItem>
                                <SelectItem value="exe">EXE</SelectItem>
                                <SelectItem value="store">Microsoft Store</SelectItem>
                                <SelectItem value="portable">Portable</SelectItem>
                                <SelectItem value="manual">Manual</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    {installerType === "winget" && (
                        <Field label="Winget ID">
                            <Input value={wingetId} onChange={(e) => setWingetId(e.target.value)} placeholder="Publisher.AppName" className="h-9" data-testid="cm-winget-id" />
                        </Field>
                    )}
                    {(installerType === "msi" || installerType === "exe") && (
                        <Field label="Installer path (UNC or local)">
                            <Input value={installerPath} onChange={(e) => setInstallerPath(e.target.value)} placeholder="\\NAS\Installers\App\setup.exe" className="h-9" data-testid="cm-installer-path" />
                        </Field>
                    )}
                    {(installerType === "msi" || installerType === "exe") && (
                        <Field label="Silent install args">
                            <Input value={silentArgs} onChange={(e) => setSilentArgs(e.target.value)} placeholder="/quiet /norestart" className="h-9" data-testid="cm-silent-args" />
                        </Field>
                    )}
                    <Field label="Notes">
                        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional context for teammates" className="h-9" data-testid="cm-notes" />
                    </Field>
                    <div className="flex items-center gap-2 text-xs text-zinc-400">
                        <input
                            id="cm-auto-link"
                            type="checkbox"
                            checked={autoLink}
                            onChange={(e) => setAutoLink(e.target.checked)}
                            data-testid="cm-auto-link"
                        />
                        <label htmlFor="cm-auto-link">Auto-link this mapping to the row (sets override to custom)</label>
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={save} disabled={saving} data-testid="cm-save-btn">
                        {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                        Create mapping
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, children }) {
    return (
        <label className="block space-y-1">
            <span className="text-xs uppercase tracking-wide text-zinc-400">{label}</span>
            {children}
        </label>
    );
}

function AppDetailsDrawer({ app, onClose }) {
    return (
        <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
            <DialogContent data-testid="app-details-drawer" className="max-w-lg">
                <DialogHeader>
                    <DialogTitle>{app.name}</DialogTitle>
                    <DialogDescription className="text-xs">
                        {app.publisher || ""}{app.version ? ` · v${app.version}` : ""}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2 py-1 text-sm">
                    <Row k="Category" v={CATEGORY_LABEL[app.category] || app.category} />
                    <Row k="Confidence" v={`${app.confidence ?? 0}%`} />
                    <Row k="App key" v={<code className="text-xs">{app.app_key}</code>} />
                    {app.app_source && <Row k="App source" v={app.app_source} />}
                    {app.install_scope && <Row k="Install scope" v={app.install_scope} />}
                    {app.architecture && <Row k="Architecture" v={app.architecture} />}
                    {app.winget_id && <Row k="Winget ID" v={app.winget_id} />}
                    {app.winget_source && <Row k="Winget source" v={app.winget_source} />}
                    {app.msi_product_code && <Row k="MSI product code" v={<code className="text-xs">{app.msi_product_code}</code>} />}
                    {app.package_family_name && <Row k="Package family" v={<code className="text-xs">{app.package_family_name}</code>} />}
                    {app.appx_package_full_name && <Row k="Package full name" v={<code className="text-xs">{app.appx_package_full_name}</code>} />}
                    {app.install_location && <Row k="Install location" v={<code className="text-xs">{app.install_location}</code>} />}
                    {app.resolution?.reason && <Row k="Resolver note" v={app.resolution.reason} />}
                    {app.resolution?.method && <Row k="Resolver method" v={app.resolution.method} />}
                    {app.override && (
                        <div className="mt-3 p-3 rounded-md bg-amber-950/30 border border-amber-800/50 text-xs space-y-1">
                            <div className="font-semibold text-amber-200">Override active</div>
                            <div className="text-zinc-300">Set by {app.override.set_by} at {app.override.set_at}</div>
                            {app.override.category && <div>Forced category: {CATEGORY_LABEL[app.override.category]}</div>}
                            {app.override.skip_reason && <div>Reason: {app.override.skip_reason}</div>}
                            {app.override.notes && <div>Notes: {app.override.notes}</div>}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex items-start gap-3">
            <span className="text-xs uppercase tracking-wide text-zinc-500 w-32 shrink-0">{k}</span>
            <span className="text-sm text-zinc-200 break-all">{v}</span>
        </div>
    );
}
